// src/pages/admin/PartnerManagementPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { auth, db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import AddPartnerForm from '../../AddPartnerForm';
import EditPartnerModal from '../../EditPartnerModal';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
    Snackbar, Alert, Box, TextField, Select, MenuItem, FormControl, InputLabel,
    CircularProgress, Button, Tooltip, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const ZONAS = [
    "Aclimação", "Carrão", "Centro (São Paulo)", "Diadema", "Guarulhos", "Ipiranga",
    "Itaquera", "Litoral", "Mooca", "Morumbi", "Saúde", "Santo André",
    "São Bernardo do Campo", "São Caetano do Sul"
];
const ITEMS_PER_PAGE = 50;

function PartnerManagementPage() {
    const user = auth.currentUser;
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [filters, setFilters] = useState({ nome: '', zona: '', status: '', gerenteId: '' });
    const [page, setPage] = useState(1);

    // Ao mudar qualquer filtro, volta para a página 1 automaticamente
    const handleFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setPage(1);
    };

    // Carrega TODOS os usuários de uma vez e ordena alfabeticamente no cliente
    const fetchAllUsers = useCallback(async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, "usuarios"));
            const usersData = snap.docs.map(d => ({ ...d.data(), id: d.id }));
            usersData.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
            setAllUsers(usersData);
        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: 'Erro ao buscar dados.', severity: 'error' });
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchAllUsers(); }, [fetchAllUsers]);

    const handleRefresh = () => {
        setPage(1);
        fetchAllUsers();
    };

    // Filtragem 100% no cliente — funciona com qualquer combinação de filtros
    const filteredUsers = useMemo(() => {
        return allUsers.filter(u => {
            const nomeOk = !filters.nome || (u.nome || '').toLowerCase().includes(filters.nome.toLowerCase());
            const zonaOk = !filters.zona || (u.zonaAtuacao || []).includes(filters.zona);
            const statusOk = !filters.status || u.status === filters.status;
            const gerenteOk = !filters.gerenteId || u.gerenteId === filters.gerenteId;
            return nomeOk && zonaOk && statusOk && gerenteOk;
        });
    }, [allUsers, filters]);

    // Paginação no cliente via slice — troca de página funciona corretamente
    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
    const paginatedUsers = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredUsers, page]);

    const handleNextPage = () => { if (page < totalPages) setPage(p => p + 1); };
    const handlePrevPage = () => { if (page > 1) setPage(p => p - 1); };

    const handleEdit = (partner) => { setEditingPartner(partner); setIsModalOpen(true); };
    const handleDelete = async (partnerId) => {
        if (window.confirm("Tem certeza?")) {
            try {
                // Exclusão via Cloud Function — o servidor grava _deletedBy com segurança
                const deletePartnerFn = httpsCallable(functions, 'deletePartner');
                await deletePartnerFn({ partnerId });
                setSnackbar({ open: true, message: 'Excluído!', severity: 'success' });
                handleRefresh();
            } catch (error) {
                console.error("Erro ao excluir parceiro:", error);
                setSnackbar({ open: true, message: 'Erro ao excluir parceiro.', severity: 'error' });
            }
        }
    };

    const gerentes = useMemo(() =>
        allUsers
            .filter(u => u.tipoUsuario === 'gerente')
            .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR')),
    [allUsers]);

    return (
        <>
            <AddPartnerForm onPartnerAdded={handleRefresh} gerentes={gerentes} />

            <Paper sx={{ p: 2, my: 4, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField name="nome" label="Nome..." size="small" value={filters.nome} onChange={handleFilterChange} />
                <FormControl size="small" sx={{ minWidth: 200 }}><InputLabel>Zona</InputLabel>
                    <Select name="zona" value={filters.zona} label="Zona" onChange={handleFilterChange}>
                        <MenuItem value=""><em>Todas</em></MenuItem>
                        {ZONAS.sort().map(z => <MenuItem key={z} value={z}>{z}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Status</InputLabel>
                    <Select name="status" value={filters.status} label="Status" onChange={handleFilterChange}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        <MenuItem value="ativo">Ativo</MenuItem>
                        <MenuItem value="inativo">Inativo</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}><InputLabel>Gerente</InputLabel>
                    <Select name="gerenteId" value={filters.gerenteId} label="Gerente" onChange={handleFilterChange}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {gerentes.map(g => <MenuItem key={g.id} value={g.id}>{g.nome}</MenuItem>)}
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={handleRefresh}>Buscar</Button>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead><TableRow>
                        <TableCell>Nome</TableCell><TableCell>E-mail</TableCell><TableCell>WhatsApp</TableCell><TableCell>Status</TableCell><TableCell align="right">Ações</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {loading ? (<TableRow><TableCell colSpan={5} align="center"><CircularProgress /></TableCell></TableRow>) : (
                            paginatedUsers.map((u) => (
                                <TableRow key={u.id}>
                                    <TableCell>{u.nome}</TableCell><TableCell>{u.email}</TableCell>
                                    <TableCell>
                                        {u.telefone ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <IconButton component="a" href={`https://wa.me/55${u.telefone.replace(/\D/g, '')}`} target="_blank" color="success"><WhatsAppIcon /></IconButton>
                                                <Typography variant="body2">{u.telefone}</Typography>
                                            </Box>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>{u.status}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Editar"><IconButton onClick={() => handleEdit(u)} color="primary"><EditIcon /></IconButton></Tooltip>
                                        {u.id !== user.uid && <Tooltip title="Excluir"><IconButton onClick={() => handleDelete(u.id)} color="error"><DeleteIcon /></IconButton></Tooltip>}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button onClick={handlePrevPage} disabled={page === 1}>Anterior</Button>
                <Typography variant="body2" color="text.secondary">
                    Página {page} de {totalPages} &nbsp;·&nbsp; {filteredUsers.length} registro{filteredUsers.length !== 1 ? 's' : ''}
                </Typography>
                <Button onClick={handleNextPage} disabled={page >= totalPages}>Próxima</Button>
            </Box>

            {isModalOpen && <EditPartnerModal user={user} partner={editingPartner} onClose={() => setIsModalOpen(false)} onPartnerUpdated={() => { setIsModalOpen(false); handleRefresh(); }} />}
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}><Alert severity={snackbar.severity}>{snackbar.message}</Alert></Snackbar>
        </>
    );
}

export default PartnerManagementPage;